import Vue from 'vue';
import Router from 'vue-router';
import NProgress from 'nprogress';

import { getToken } from '../lib/auth';
import store from '../store';
import i18n from '../i18n';

const Main = () => import('@/components/Main');
const Login = () => import('@/components/Login');
const NotFound = () => import('@/components/NotFound');

// Auth module
const ForgotPassword = () => import('@/components/ForgotPassword');
const Register = () => import('@/components/Register');
const ConfirmAccount = () => import('@/components/ConfirmAccount');
const ResetPassword = () => import('@/components/ResetPassword');
const ResendConfirmation = () => import('@/components/ResendConfirmation');
const Dashboard = () => import('@/components/Dashboard');
const PlayerConfirmation = () => import('@/components/PlayerConfirmation');
const CoachConfirmation = () => import('@/components/CoachConfirmation');
const CoachConfirmationFromCompany = () => import('@/components/CoachConfirmationFromCompany');
const StaffConfirmation = () => import('@/components/StaffConfirmation');
const Settings = () => import('@/components/Settings');

// User module
const Profile = () => import('@/components/user/Profile');
const NotificationsHistory = () => import('@/components/user/NotificationsHistory');
const BillingAddress = () => import('@/components/user/BillingAddress');

// Seasons module
const Season = () => import('@/components/seasons/Season');
const SeasonsArchive = () => import('@/components/seasons/Archive');

// Exercise module
const NewExercise = () => import('@/components/exercises/New');
const EditExercise = () => import('@/components/exercises/Edit');
const Exercises = () => import('@/components/exercises/list/Container');
const ShowExercise = () => import('@/components/exercises/Show');

// Players module
const PlayerForm = () => import('@/components/players/Form');
const Players = () => import('@/components/players/List');
const ShowPlayer = () => import('@/components/players/Show');
const ObjectiveList = () => import('@/components/players/ObjectiveList');
const PlayerRatings = () => import('@/components/players/RatingsForm');
const PlayerVideotags = () => import('@/components/players/Videotags');

// Trainings module
const Trainings = () => import('@/components/trainings/List');
const TrainingForm = () => import('@/components/trainings/Form');
const ShowTraining = () => import('@/components/trainings/Show');
const TrainingAttendances = () => import('@/components/trainings/Attendances');
const TrainingVideo = () => import('@/components/trainings/Video');
const TrainingVideotags = () => import('@/components/trainings/Videotags');

// Matches module
const Matches = () => import('@/components/matches/List');
const MatchForm = () => import('@/components/matches/form/Form');
const MatchVideo = () => import('@/components/matches/Video');
const MatchVideotags = () => import('@/components/matches/Videotags');
const ShowMatch = () => import('@/components/matches/Show');

// Statistics module
const Statistics = () => import('@/components/statistics/Statistics');
const Goals = () => import('@/components/statistics/Goals');
const Attendances = () => import('@/components/statistics/Attendances');

// Videotags module
const VideotagTableau = () => import('@/components/videotags/Tableau');
const VideotagArchive = () => import('@/components/videotags/Archive');
const VideotagVideoTrainings = () => import('@/components/videotags/VideoTrainings');
const VideotagVideoMatches = () => import('@/components/videotags/VideoMatches');
const VideotagVideoManager = () => import('@/components/videotags/VideoManager');
const VideotagVideoMatchForm = () => import('@/components/videotags/VideoMatchForm');
const VideotagVideoTrainingForm = () => import('@/components/videotags/VideoTrainingForm');
const VideotagsMatch = () => import('@/components/videotags/TagsMatch');
const VideotagsTraining = () => import('@/components/videotags/TagsTraining');

// Subscriptions module
const Subscriptions = () => import('@/components/subscriptions/List');

// Coaches module
const Coaches = () => import('@/components/coaches/List');
const CoachForm = () => import('@/components/coaches/Form');
const Coach = () => import('@/components/coaches/Show');
const SendMessage = () => import('@/components/coaches/SendMessage');

// Staff module
const Staff = () => import('@/components/staff/List');
const StaffForm = () => import('@/components/staff/Form');
const StaffSingle = () => import('@/components/staff/Show');

//Coupons Releted Component
const CouponConfermaRegistrazione = () => import('@/components/ConfermaRegistrazione');

// Academy
const Academy = () => import('@/components/academy/List');

// Legal stuff
const TOS = () => import('@/components/TOS');

Vue.use(Router);

const router = new Router({
  mode: 'history',
  scrollBehavior() {
    return false;
  },
  routes: [
    {
      path: '/',
      component: Main,
      meta: { auth: true, main: true },
      children: [
        {
          path: '',
          name: 'Dashboard',
          component: Dashboard,
          meta: { free: true },
        },

        {
          path: 'profile',
          name: 'Profile',
          component: Profile,
          meta: { free: true },
        },
        {
          path: 'settings',
          name: 'Settings',
          component: Settings,
          meta: { free: true },
        },
        {
          path: 'subscriptions',
          name: 'Subscriptions',
          component: Subscriptions,
          meta: { free: true },
        },
        {
          path: 'season',
          name: 'Season',
          component: Season,
          meta: { roles: ['coach'] },
        },
        {
          path: 'notifications',
          name: 'Notifications',
          component: NotificationsHistory,
        },
        {
          path: 'billing-address',
          name: 'BillingAddress',
          component: BillingAddress,
          meta: { free: true },
        },
        {
          path: 'seasons/archive',
          name: 'SeasonsArchive',
          component: SeasonsArchive,
          meta: { roles: ['coach'] },
        },
        {
          path: 'coaches',
          name: 'Coaches',
          component: Coaches,
        },
        {
          path: 'coaches/new',
          name: 'NewCoach',
          component: CoachForm,
        },
        {
          path: 'coaches/message',
          name: 'SendMessage',
          component: SendMessage,
        },
        {
          path: 'coaches/:id',
          name: 'Coach',
          component: Coach,
          props: true,
        },
        {
          path: 'staff',
          name: 'Staff',
          component: Staff,
        },
        {
          path: 'staff/new',
          name: 'NewStaff',
          component: StaffForm,
        },
        {
          path: 'staff/:id',
          name: 'StaffSingle',
          component: StaffSingle,
          props: true,
        },
        {
          path: 'my-exercises',
          component: Exercises,
          name: 'MyExercises',
          meta: { roles: ['coach', 'staff'], free: true },
        },
        {
          path: 'exercises',
          component: Exercises,
          name: 'PublicExercises',
          props: { isPublic: 1 },
        },
        {
          path: 'company-exercises',
          component: Exercises,
          name: 'CompanyExercises',
          props: { companyOnly: true },
        },
        {
          path: 'exercises-to-approve',
          component: Exercises,
          name: 'ExercisesToApprove',
          props: { companyOnly: true, toApprove: 1 },
        },
        {
          path: 'exercises/new',
          name: 'NewExercise',
          component: NewExercise,
          meta: { roles: ['coach', 'staff'] },
        },
        {
          path: 'exercises/:id',
          name: 'ShowExercise',
          component: ShowExercise,
          meta: { roles: ['coach', 'staff'], free: true },
        },
        {
          path: 'exercises/:id/edit',
          name: 'EditExercise',
          component: EditExercise,
          meta: { roles: ['coach', 'staff'] },
        },
        {
          path: 'my-kicks',
          name: 'MyKicks',
          component: Exercises,
          meta: { roles: ['coach', 'staff'] },
          props: { kicks: true },
        },
        {
          path: 'kicks',
          name: 'Kicks',
          component: Exercises,
          props: { kicks: true, isPublic: 1 },
        },
        {
          path: 'company-kicks',
          component: Exercises,
          name: 'CompanyKicks',
          props: { companyOnly: true, kicks: true },
        },
        {
          path: 'kicks-to-approve',
          component: Exercises,
          name: 'KicksToApprove',
          props: { companyOnly: true, toApprove: 1, kicks: true },
        },
        {
          path: 'kicks/new',
          name: 'NewKick',
          component: NewExercise,
          meta: { roles: ['coach', 'staff'] },
          props: { kicks: true },
        },
        {
          path: 'kicks/:id',
          name: 'ShowKick',
          component: ShowExercise,
          meta: { roles: ['coach', 'staff'] },
          props: { kicks: true },
        },
        {
          path: 'kicks/:id/edit',
          name: 'EditKick',
          component: EditExercise,
          meta: { roles: ['coach', 'staff'] },
          props: { kicks: true },
        },
        {
          path: 'players',
          name: 'Players',
          component: Players,
          meta: { roles: ['coach', 'staff'], season: true },
        },
        {
          path: 'players/new',
          name: 'NewPlayer',
          component: PlayerForm,
          meta: { roles: ['coach', 'staff'], season: true, oldSeasons: false },
        },
        {
          path: 'players/base-technique',
          name: 'PlayersBaseTechnique',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: { identifiers: ['base_technique'] },
        },
        {
          path: 'players/individual-tactics',
          name: 'PlayersIndividualTactics',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: { identifiers: ['possession', 'non_possession'] },
        },
        {
          path: 'players/goalkeeper-technique',
          name: 'PlayersGoalkeeperTechnique',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: {
            identifiers: ['goalkeeping', 'individual_goalkeeping', 'section_goalkeeping'],
            goalkeeper: 1,
          },
        },
        {
          path: 'players/motor-skills',
          name: 'PlayersMotorSkills',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: { identifiers: ['conditional_motor', 'coordinative_motor'], goalkeeper: 0 },
        },
        {
          path: 'players/motor-skills-goalkeeper',
          name: 'PlayersMotorSkillsGoalkeeper',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: {
            identifiers: ['conditional_motor_goalkeeper', 'coordinative_motor'],
            goalkeeper: 1,
          },
        },
        {
          path: 'players/collective-tactics',
          name: 'PlayersCollectiveTactics',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: { identifiers: ['collective_possession', 'collective_non_possession'] },
        },
        {
          path: 'players/kicks',
          name: 'PlayersKicks',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: { identifiers: ['kicks'] },
        },
        {
          path: 'players/game-modules-situations',
          name: 'PlayersModulesSituations',
          component: ObjectiveList,
          meta: { roles: ['coach', 'staff'], season: true },
          props: { identifiers: ['game_modules', 'game_situations'] },
        },
        {
          path: 'players/:id',
          name: 'ShowPlayer',
          component: ShowPlayer,
          meta: { roles: ['coach', 'staff'], season: true },
        },
        {
          path: 'players/:id/edit',
          name: 'EditPlayer',
          component: PlayerForm,
          meta: { roles: ['coach', 'staff'], season: true, oldSeasons: false },
        },
        {
          path: 'players/:id/ratings',
          name: 'PlayerRatings',
          component: PlayerRatings,
          meta: { roles: ['coach', 'staff'], season: true, oldSeasons: false },
          props: true,
        },
        {
          path: 'players/:id/videotags',
          name: 'PlayerVideotags',
          component: PlayerVideotags,
          meta: { roles: ['coach', 'staff'], season: true, videotags: true },
        },
        {
          path: 'trainings',
          name: 'Trainings',
          component: Trainings,
        },
        {
          path: 'trainings/new',
          name: 'NewTraining',
          component: TrainingForm,
          props: true,
          meta: { oldSeasons: false },
        },
        {
          path: 'trainings/attendances',
          name: 'NewTrainingAttendances',
          component: TrainingAttendances,
          props: { noTraining: true },
          meta: { oldSeasons: false },
        },
        {
          path: 'trainings/:id',
          name: 'ShowTraining',
          component: ShowTraining,
        },
        {
          path: 'trainings/:id/edit',
          name: 'EditTraining',
          component: TrainingForm,
          meta: { oldSeasons: false },
        },
        {
          path: 'trainings/:id/attendances',
          name: 'TrainingAttendances',
          component: TrainingAttendances,
          meta: { oldSeasons: false },
        },
        {
          path: 'trainings/:id/video',
          name: 'TrainingVideo',
          component: TrainingVideo,
        },
        {
          path: 'trainings/:id/videotags/:videoId',
          name: 'TrainingVideotags',
          component: TrainingVideotags,
          meta: { oldSeasons: false, videotags: true, videotagMenu: true, hideBreadcrumbs: true },
        },
        {
          path: 'academy',
          name: 'Academy',
          component: Academy,
        },
        {
          path: 'matches',
          name: 'Matches',
          component: Matches,
        },
        {
          path: 'matches/new',
          name: 'NewMatch',
          component: MatchForm,
          props: true,
          meta: { oldSeasons: false },
        },
        {
          path: 'matches/:id',
          name: 'ShowMatch',
          component: ShowMatch,
        },
        {
          path: 'matches/:id/edit',
          name: 'EditMatch',
          component: MatchForm,
          props: true,
          meta: { oldSeasons: false },
        },
        {
          path: 'matches/:id/video',
          name: 'MatchVideo',
          component: MatchVideo,
        },
        {
          path: 'matches/:id/videotags/:videoId',
          name: 'MatchVideotags',
          component: MatchVideotags,
          meta: { oldSeasons: false, videotags: true, videotagMenu: true, hideBreadcrumbs: true },
        },
        {
          path: 'statistics/objectives',
          name: 'Statistics',
          component: Statistics,
        },
        {
          path: 'statistics/goals',
          name: 'Goals',
          component: Goals,
        },
        {
          path: 'statistics/attendances',
          name: 'Attendances',
          component: Attendances,
        },
        {
          path: 'videotags/tableau',
          name: 'VideotagTableau',
          component: VideotagTableau,
          meta: { videotags: true },
        },
        {
          path: 'videotags/archive',
          name: 'VideotagArchive',
          component: VideotagArchive,
          meta: { videotags: true },
        },
        {
          path: 'videotags/video/trainings',
          name: 'VideotagVideoTrainings',
          component: VideotagVideoTrainings,
          meta: { videotags: true },
        },
        {
          path: 'videotags/video/matches',
          name: 'VideotagVideoMatches',
          component: VideotagVideoMatches,
          meta: { videotags: true },
        },
        {
          path: 'videotags/video/manager',
          name: 'VideotagVideoManager',
          component: VideotagVideoManager,
          meta: { videotagPro: true },
        },
        {
          path: 'videotags/video/match/new',
          name: 'VideotagNewVideoMatch',
          component: VideotagVideoMatchForm,
          meta: { oldSeasons: false, videotags: true },
        },
        {
          path: 'videotags/video/match/:id/edit',
          name: 'VideotagEditVideoMatch',
          component: VideotagVideoMatchForm,
          meta: { oldSeasons: false, videotags: true },
        },
        {
          path: 'videotags/video/training/new',
          name: 'VideotagNewVideoTraining',
          component: VideotagVideoTrainingForm,
          meta: { oldSeasons: false, videotags: true },
        },
        {
          path: 'videotags/video/training/:id/edit',
          name: 'VideotagEditVideoTraining',
          component: VideotagVideoTrainingForm,
          meta: { oldSeasons: false, videotags: true },
        },
        {
          path: 'videotags/match/:id',
          name: 'VideotagsMatch',
          component: VideotagsMatch,
          meta: { videotags: true },
        },
        {
          path: 'videotags/training/:id',
          name: 'VideotagsTraining',
          component: VideotagsTraining,
          meta: { videotags: true },
        },
      ],
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: { noHelp: true },
    },
    {
      path: '/register',
      name: 'Register',
      component: Register,
      props: true,
      meta: { noHelp: true },
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPassword,
      props: true,
      meta: { noHelp: true },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: { noHelp: true },
    },
    {
      path: '/confirm',
      name: 'ConfirmAccount',
      component: ConfirmAccount,
      meta: { noHelp: true },
    },
    {
      path: '/confirm/player',
      name: 'PlayerConfirmation',
      component: PlayerConfirmation,
      meta: { noHelp: true },
    },
    {
      path: '/confirm/coach',
      name: 'CoachConfirmation',
      component: CoachConfirmation,
      meta: { noHelp: true },
    },
    {
      path: '/confirm/coach/:id/:companyId',
      name: 'CoachCompanyConfirmation',
      component: CoachConfirmationFromCompany,
      meta: { noHelp: true },
    },
    {
      path: '/confirm/staff',
      name: 'StaffConfirmation',
      component: StaffConfirmation,
      meta: { noHelp: true },
    },
    {
      path: '/resend-confirmation',
      name: 'ResendConfirmation',
      component: ResendConfirmation,
      meta: { noHelp: true },
    },
    {
      path: '/conferma-registrazione/:coupenToken',
      name: 'CouponConfermaRegistrazione',
      component: CouponConfermaRegistrazione,
    },
    {
      path: '/tos',
      name: 'TOS',
      component: TOS,
    },
    {
      path: '/esercizi/*',
      redirect: '/register',
    },
    {
      path: '*',
      component: NotFound,
    },
  ],
});

// Check if the user is authenticated
router.beforeEach((to, from, next) => {
  NProgress.start();
  // if (store.state.breadcrumbs.length > 0) {
  //   store.commit('resetBreadcrumbs');
  // }

  if (to.meta.auth && !getToken()) {
    next({
      path: '/login',
      query: { redirect: to.fullPath, error: 1 },
    });
  } else if (!to.meta.free && store.state.currentUser && !store.getters.userHasSubscription) {
    next('/subscriptions?error=1');
  } else if (to.meta.videotags && store.state.currentUser && !store.state.currentUser.videotags) {
    store.commit('notification', {
      color: 'error',
      text: i18n.t('videotags.noAccess'),
    });
    next(false);
  } else if (to.meta.videotagPro && store.state.currentUser && store.state.currentUser.videotags < 2) {
    store.commit('notification', {
      color: 'error',
      text: i18n.t('videotags.pro'),
    });
    next(false);
  } else if (to.meta.oldSeasons === false && store.state.season) {
    store.commit('notification', {
      color: 'error',
      text: i18n.t('seasons.oldSeasonAccess'),
    });
    if (from.path === '/') {
      next('/');
    } else {
      next(false);
    }
  } else {
    next();
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js